<template>
  <el-dialog
    :visible="visible"
    @close="$emit('cancelled')"
    :title="$t('loyalty.title.adjust_points')"
    width="600px"
    append-to-body
    custom-class="points-adjustment-modal">
    <div class="px-6 py-4">
      <el-form :model="form" :rules="rules" ref="adjustForm" label-width="180px" class="adjust-form">
        <el-form-item :label="$t('loyalty.label.program')" prop="program_id" class="mb-6">
          <el-select
            v-model="form.program_id"
            :placeholder="$t('loyalty.placeholder.select_program')"
            style="width: 100%">
            <el-option
              v-for="program in programs"
              :key="program.id"
              :label="program.name"
              :value="program.id">
            </el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.points')" prop="points" class="mb-6">
          <el-input-number
            v-model="form.points"
            :min="-999999"
            :max="999999"
            :placeholder="$t('loyalty.placeholder.points_adjustment')"
            class="w-full">
          </el-input-number>
          <p class="text-xs text-gray-500 mt-1">
            {{ $t('loyalty.placeholder.points_adjustment_hint') }}
          </p>
        </el-form-item>

        <el-form-item :label="$t('loyalty.label.description')" prop="description" class="mb-6">
          <el-input
            type="textarea"
            v-model="form.description"
            :rows="4"
            :placeholder="$t('loyalty.placeholder.adjustment_description')"
            class="w-full">
          </el-input>
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer px-6 py-4 bg-gray-50 flex justify-end space-x-3">
      <button
        @click="$emit('cancelled')"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
        {{ $t('button.cancel') }}
      </button>
      <button
        @click="save"
        :disabled="saving"
        class="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
        <span v-if="saving" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('status.loading') }}
        </span>
        <span v-else>{{ $t('button.save') }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'PointsAdjustmentModal',
  mixins: [ErrorHandlingMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: [Number, String],
      required: true
    },
    programId: {
      type: Number,
      default: null
    },
    programs: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      saving: false,
      form: {
        program_id: null,
        points: 0,
        description: ''
      }
    }
  },
  computed: {
    rules () {
      return {
        program_id: [
          { required: true, message: this.$t('loyalty.validation.program_required'), trigger: 'change' }
        ],
        points: [
          { required: true, message: this.$t('loyalty.validation.points_required'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value === 0) {
                callback(new Error(this.$t('loyalty.validation.points_not_zero')))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        description: [
          { required: true, message: this.$t('loyalty.validation.description_required'), trigger: 'blur' },
          { max: 500, message: 'Description cannot exceed 500 characters', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    visible (newVal) {
      if (newVal) {
        this.form.program_id = this.programId || (this.programs.length > 0 ? this.programs[0].id : null)
        this.form.points = 0
        this.form.description = ''
        if (this.$refs.adjustForm) {
          this.$refs.adjustForm.clearValidate()
        }
      }
    },
    programId (newVal) {
      if (newVal && this.visible) {
        this.form.program_id = newVal
      }
    }
  },
  methods: {
    async save () {
      this.$refs.adjustForm.validate(async (valid) => {
        if (!valid) return

        try {
          this.saving = true
          await this.$store.dispatch('loyalty/customer/adjustPoints', {
            customerId: this.customerId,
            programId: this.form.program_id,
            points: this.form.points,
            description: this.form.description
          })
          this.$emit('saved')
        } catch (error) {
          console.error('Error adjusting points:', error)
          this.globalErrHandle(error)
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.points-adjustment-modal ::v-deep .el-dialog__body {
  padding: 0;
}

.adjust-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.adjust-form ::v-deep .el-input__inner,
.adjust-form ::v-deep .el-textarea__inner {
  border-radius: 0.5rem;
  border-color: #d1d5db;
}

.adjust-form ::v-deep .el-input__inner:focus,
.adjust-form ::v-deep .el-textarea__inner:focus {
  border-color: #3b82f6;
}
</style>
